/**
 * Enhanced authentication utilities with "unlimited" upload support
 */
import { authStorage } from "./localStorage";
import { recordTesterRequest } from "@/features/testing/testerTasks";
import { supabase } from "./supabase";
import { pollForCompletion } from "./polling";
import { announceUsageLimit } from "@/lib/usageLimit";
import { track } from "@/lib/analytics/analytics";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { validateFileType } from "@/utils/fileValidation";
import { getUploadId, UPLOAD_TIMEOUT_MS } from "./uploadPolicy";

const SLOW_API_REQUEST_THRESHOLD_MS = 3000;

interface UploadProgress {
  loaded: number;
  total: number;
  storageStrategy: "memory" | "disk" | "cloud";
  phase: "uploading" | "processing" | "chunking" | "complete";
  estimatedTime?: string;
}

interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  forceStorage?: "memory" | "disk" | "cloud";
  title: string;
  description?: string;
  subject_id?: string;
}

/**
 * Get a fresh access token with automatic refresh if near expiry
 */
let refreshPromise: Promise<string | null> | null = null;
let cachedCsrfToken: string | null = null;

/**
 * Fetch a new CSRF token from the server
 */
export async function refreshCsrfToken(): Promise<string | null> {
  try {
    const API_URL = import.meta.env.VITE_API_URL || "";
    const response = await fetch(`${API_URL}/api/csrf-token`);
    if (response.ok) {
      const data = await response.json();
      cachedCsrfToken = data.csrfToken;
      return cachedCsrfToken;
    }
  } catch (error) {
    console.error("[CSRF] Failed to fetch token:", error);
  }
  return null;
}


/**
 * Get a fresh access token with automatic refresh if near expiry
 */
export async function getFreshAuthToken(): Promise<string | null> {
  // If a refresh is already in progress, return the existing promise
  if (refreshPromise) {
    return refreshPromise;
  }

  try {
    refreshPromise = (async () => {
      try {
        const start = Date.now();
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error || !session) {
          return null;
        }

        // Check if token expires within 5 minutes (300 seconds)
        const expiresIn = (session.expires_at || 0) - Date.now() / 1000;
        const fiveMinutes = 5 * 60;

        // If token is about to expire, refresh it
        if (expiresIn < fiveMinutes) {
          const {
            data: { session: newSession },
            error: refreshError,
          } = await supabase.auth.refreshSession();

          if (refreshError || !newSession) {
            return session.access_token; // Return old token as fallback
          }

          return newSession.access_token;
        }

        return session.access_token;
      } catch (error) {
        // Suppress auth logs in production via global logger
        return null;
      }
    })();

    return await refreshPromise;
  } finally {
    // Clean up the promise after a short delay
    setTimeout(() => {
      refreshPromise = null;
    }, 100);
  }
}

/**
 * Get authentication headers with fresh token
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getFreshAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (cachedCsrfToken) {
    headers["X-CSRF-Token"] = cachedCsrfToken;
  }

  return headers;
}

/**
 * Enhanced file upload with progress tracking and strategy detection
 */
export async function uploadFileWithProgress(
  file: File,
  options: UploadOptions,
): Promise<{ url: string; message: string; metadata: any }> {
  // Fail fast, client-side, before ever touching the network — an
  // unsupported file or one over the size cap can't succeed no matter how
  // good the connection is, so there's no reason to upload it first only to
  // have the server reject it and report back the same thing slower.
  if (!(options as any)._csrfRetry) {
    const validation = validateFileType(file);
    if (!validation.isValid) {
      throw new Error(validation.error || "This file can't be uploaded.");
    }
  }

  const uploadOwnerId = authStorage.getUser()?.id;
  const token = await getFreshAuthToken();
  if (!token) {
    throw new Error("No authentication token available");
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("title", options.title);
  formData.append("upload_id", getUploadId(file, options.title, options.description, options.subject_id));
  if (options.description) {
    formData.append("description", options.description);
  }
  if (options.forceStorage) {
    formData.append("forceStorage", options.forceStorage);
  }
  if (options.subject_id) {
    formData.append("subject_id", options.subject_id);
  }

  const estimatedStrategy = getStorageStrategy(file.size);

  if (options.onProgress) {
    options.onProgress({
      loaded: 0,
      total: file.size,
      storageStrategy: estimatedStrategy,
      phase: "uploading",
    });
  }

  const API_URL = import.meta.env.VITE_API_URL || "";
  const headers = await getAuthHeaders();

  // Important: for FormData, we MUST NOT set Content-Type manually,
  // browser needs to set it with the boundary string
  delete headers["Content-Type"];

  // A dropped 3G connection can fail a whole multipart request after the
  // browser has already read the file. Retry the complete upload once after a
  // short backoff, keeping the same upload_id so a lost response cannot
  // create a second document. The selected File remains available for retry.
  let response: Response | undefined;
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
    try {
      response = await fetch(`${API_URL}/api/documents/upload`, {
        method: "POST",
        headers,
        body: formData,
        signal: controller.signal,
      });
      break;
    } catch (err) {
      lastError = err;
      if (attempt === 0) {
        options.onProgress?.({ loaded: 0, total: file.size, storageStrategy: estimatedStrategy, phase: "uploading", estimatedTime: "Retrying…" });
        await new Promise((resolve) => setTimeout(resolve, 750));
      }
    } finally {
      clearTimeout(timeoutId);
    }
  }
  if (!response) {
    // fetch() throws (rather than resolving with a response) for an aborted
    // request or a genuine network failure. Keep those messages actionable.
    if (lastError instanceof DOMException && lastError.name === "AbortError") {
      throw new Error("Upload timed out twice. Check your connection and try again.");
    }
    throw new Error("Network connection lost after retrying. Check your connection and try again.");
  }

  // Update CSRF token if present
  const newToken = response.headers.get("X-CSRF-Token");
  if (newToken) {
    cachedCsrfToken = newToken;
  }

  if (options.onProgress) {
    options.onProgress({
      loaded: Math.floor(file.size * 0.7),
      total: file.size,
      storageStrategy: estimatedStrategy,
      phase: "uploading",
      estimatedTime: "~10s",
    });
  }

  if (!response.ok) {
    // Handle CSRF retry for upload too
    if (response.status === 403 && ! (options as any)._csrfRetry) {
      const errorData = await response.clone().json().catch(() => ({}));
      if (errorData.error?.toLowerCase().includes("csrf")) {
        console.log("[CSRF] Upload failed due to CSRF, refreshing and retrying...");
        await refreshCsrfToken();
        return uploadFileWithProgress(file, { ...options, _csrfRetry: true } as any);
      }
    }

    const errorData = await response.json().catch(() => ({}));
    // The server sends a readable `message` beside a machine `error` code
    // (e.g. STORAGE_LIMIT_REACHED, PROJECT_STORAGE_FULL). Show the message;
    // the bare code used to reach students verbatim.
    const serverMessage: string | undefined = errorData.message || undefined;
    if (response.status === 413) {
      throw new Error(`File too large: ${errorData.error || "please upload a smaller file."}`);
    }
    if (response.status === 503) {
      throw new Error(serverMessage || "Storage is temporarily unavailable. Please try again in a moment.");
    }
    if (response.status >= 500) {
      throw new Error(serverMessage || errorData.error || "The server is having trouble right now. Please try again in a moment.");
    }
    throw new Error(
      serverMessage || errorData.error || `Upload failed (${response.status}). Please try again.`,
    );
  }

  const result = await response.json();

  if (options.onProgress) {
    options.onProgress({
      loaded: file.size,
      total: file.size,
      storageStrategy:
        result.file_metadata?.storage_strategy || estimatedStrategy,
      phase: "complete",
    });
  }

  recordTesterRequest(uploadOwnerId, "/api/documents/upload", "POST");
  return result;
}

function getStorageStrategy(fileSize: number): "memory" | "disk" | "cloud" {
  const SMALL_FILE = 5 * 1024 * 1024;
  const LARGE_FILE = 500 * 1024 * 1024;

  if (fileSize < SMALL_FILE) return "memory";
  if (fileSize < LARGE_FILE) return "disk";
  return "cloud";
}

export function monitorDocumentProgress(
  documentId: string,
  onProgress: (status: any) => void,
  interval: number = 2000,
): () => void {
  return pollForCompletion(async (signal) => {
    const response = await authFetch(`/api/documents/${documentId}/status`, { signal });
    if (!response.ok) throw new Error("Document status unavailable");
    const status = await response.json();
    if (signal.aborted) return true;
    onProgress(status);
    return ["ready", "completed", "failed"].includes(status.status);
  }, interval, () => onProgress({ status: "timeout" }));
}

export async function authFetch(
  url: string,
  options: RequestInit & { _retry?: boolean; _csrfRetry?: boolean; /** Caller shows its own daily-limit UI; skip the global red toast. */ quietDailyLimit?: boolean } = {},
): Promise<Response> {
  try {
    const requestUserId = authStorage.getUser()?.id;
    const headers = await getAuthHeaders();

    if (options.body instanceof FormData) {
      delete headers["Content-Type"];
    }

    const API_URL = import.meta.env.VITE_API_URL || "";
    const requestStart = performance.now();
    const response = await fetch(`${API_URL}${url}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });
    const durationMs = Math.round(performance.now() - requestStart);
    if (durationMs > SLOW_API_REQUEST_THRESHOLD_MS) {
      let path = url;
      try {
        path = new URL(url, window.location.origin).pathname;
      } catch {
        // url wasn't a valid path — fall back to the raw value.
      }
      track(ANALYTICS_EVENTS.SLOW_API_REQUEST, { path, duration_ms: durationMs, status: response.status });
    }

    // Update CSRF token from response header if present
    const newToken = response.headers.get("X-CSRF-Token");
    if (newToken) {
      cachedCsrfToken = newToken;
    }

    if (response.status === 429) {
      const limit = await response.clone().json().catch(() => null);
      const daily = limit?.code === 'DAILY_AI_LIMIT_EXCEEDED' || limit?.error === 'DAILY_AI_LIMIT_EXCEEDED';
      if (daily && options.quietDailyLimit) return response;
      // Any AI surface that doesn't handle the limit itself still gets the
      // upgrade view: AppShell listens for this and opens the paywall (it
      // cancels the event to say it handled it). Without a listener, fall back
      // to the usage-limit modal.
      if (daily && typeof window !== "undefined") {
        const handled = !window.dispatchEvent(new CustomEvent("novari:daily-limit", { cancelable: true, detail: { resetsAt: limit?.retry_at } }));
        if (handled) return response;
      }
      announceUsageLimit({ kind: daily ? "daily" : "rate", message: daily ? limit?.message : undefined, resetsAt: limit?.retry_at });
    }

    // Handle CSRF failures
    if (response.status === 403 && !options._csrfRetry) {
      try {
        const errorData = await response.clone().json();
        if (errorData.error?.toLowerCase().includes("csrf")) {
          console.log("[CSRF] Token invalid or missing, refreshing...");
          await refreshCsrfToken();
          return authFetch(url, { ...options, _csrfRetry: true });
        }
      } catch (e) {
        // Not a JSON response or doesn't have the expected error format
      }
    }

    if (response.status === 401 && !options._retry) {
      const freshToken = await getFreshAuthToken();
      if (freshToken) {
        return authFetch(url, { ...options, _retry: true });
      }
    }

    if (response.ok && options.method && options.method.toUpperCase() !== "GET" &&
      /^\/api\/(quizzes|flashcards|study|mastery|user|curriculum|papers|question-sets|academic-bank|review|planner)(\/|$)/.test(url)) {
      // Mark only the planning projections stale. Existing offline answer replay
      // uses this same boundary, so independent study also refreshes the plan.
      void import("./queryClient").then(({ queryClient }) => {
        for (const name of ["adaptive-study", "curriculum-coverage", "recommend-mode", "up-next", "daily-queue"]) {
          void queryClient.invalidateQueries({ queryKey: [name], refetchType: "none" });
        }
      });
    }
    if (response.ok) recordTesterRequest(requestUserId, url, options.method);
    return response;
  } catch (error) {
    // Don't show an error toast for intentional request cancellations
    // (e.g. React StrictMode double-mount cleanup, component unmount mid-request)
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    // Deliberately silent: connectivity is communicated once, by
    // SlowConnectionModal (full-screen, only for a genuinely unusable
    // connection), and each screen renders its own calm error state (e.g.
    // Today's "That didn't load"). Firing a destructive toast per failed
    // request stacked 2–3 identical "No internet" cards over the content
    // — noise, and not in the coach's voice. Callers still catch and handle.
    throw error;
  }
}

export async function isAuthenticated(): Promise<boolean> {
  try {
    const token = await getFreshAuthToken();
    return !!token;
  } catch {
    return false;
  }
}

export async function getCurrentUser() {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.user || null;
  } catch (error) {
    // Silent error in production
    return null;
  }
}

export async function logout() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getStorageStats() {
  try {
    const response = await authFetch("/api/documents");
    if (!response.ok) throw new Error("Failed to fetch storage stats");
    const data = await response.json();
    return data.metadata || {};
  } catch (error) {
    // Silent stats error
    return null;
  }
}
